import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

import withLocalization from '../../../hoc/withLocalization';
import LoadingSpinner from '../../../elements/LoadingSpinner';
import GenericList from '../../../components/GenericList/GenericList';
import GenericFilter from '../../../components/GenericList/GenericFilter';
import Button from '../../../components/CustomButton/CustomButton';
import Checkbox from '../../../components/CustomCheckbox/CustomCheckbox';
import Select from '../../../elements/Select';
import { SingleUserGroupsWidget } from '../../../components/GenericForm/Widgets/UserGroupWidget.js';
import UserWeeklyOvertimeSettingModal, { BulkDeactivateOvertimeModal } from './UserWeeklyOvertimeSettingModal';
import { shortDateFormat } from '../../../utils/dateFormat';

const FIELD_FILTER_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'true', label: 'enabled' },
    { value: 'false', label: 'disabled' },
];

const BULK_FIELDS = [
    { value: 'auto_break', label: 'Auto break', description: 'If disabled then break will be not deducted from timesheet and absence', type: 'boolean', hasValidity: false, settingIdKey: null },
    { value: 'follow_schedule_for_absence', label: 'Follow schedule shift hours while taking absence', description: 'Uses primary shift or company hours when disabled.', type: 'boolean', hasValidity: false, settingIdKey: null },
    { value: 'leave_setting', label: 'Leave Setting', description: 'Assign specific leave types to selected users.', type: 'boolean', hasValidity: false, settingIdKey: null },
    { value: 'exclude_break_in_working_hours_rule', label: 'Shift code excludes break', description: 'Break time is not counted in shift code\'s working hours', type: 'boolean', hasValidity: false, settingIdKey: null },
    { value: 'allow_weekly_overtime', label: 'Weekly Overtime Settings', description: 'Apply weekly overtime settings to selected users.', type: 'modal', modalComponent: UserWeeklyOvertimeSettingModal, initialTab: 'weeklyOvertimeSetting', hasValidity: true, settingIdKey: 'weekly_overtime_id' },
    { value: 'allow_overtime_cap_settings', label: 'Overtime Cap Settings', description: 'Apply overtime cap settings to selected users.', type: 'modal', modalComponent: UserWeeklyOvertimeSettingModal, initialTab: 'overtimeCapSetting', hasValidity: true, settingIdKey: 'overtime_cap_setting_id' },
    { value: 'allow_user_for_auto_timesheet', label: 'Auto time recording', description: 'Automatically create timesheets according to schedule', type: 'boolean', hasValidity: false, settingIdKey: null },
];

class BulkUserProfileUpdate extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedField: null,
            selectedLeaveTypeCode: null,
            filters: { name: '', fieldFilter: '' },
            appliedFilters: { name: '', fieldFilter: '' },
            userGroupsValue: '',
            reloadKey: Date.now(),
            selectedIds: [],
            currentPageRows: [],
            seenIds: [],
            allSeenEnabledIds: [],
            loading: false,
            fieldConfig: {},
            weeklyOvertimeSettingMap: {},
            overtimeCapSettingMap: {},
            fieldCounts: null,
            bulkModalOpen: false,
            bulkDeactivateModalOpen: false,
            deactivateUserId: null,
            page: 0,
            isPageAddedOrEdited: false,
        };
    }

    handlePageChange = (page, isPageAddedOrEdited) => {
        this.setState({ page, isPageAddedOrEdited });
    };

    async componentDidMount() {
        try {
            const res = await this.props.userStore.bulkUpdateUserFieldConfig();
            const weeklyOvertimeSettingMap = Object.fromEntries((res.weeklyOvertimeSettings || []).map(s => [s.id, s.name]));
            const overtimeCapSettingMap = Object.fromEntries((res.overtimeCapSettings || []).map(s => [s.id, s.name]));
            this.setState({ fieldConfig: res.fieldConfig || {}, weeklyOvertimeSettingMap, overtimeCapSettingMap });
            // refresh commonStore.config so the "Not Default" leave type list reflects any recent Control Panel toggle without needing a full page reload
            this.props.commonStore.setAppLoaded();
        } catch (err) {
            console.error(err);
        }
    }

    setFilter = (field, value) => {
        this.setState(prev => ({ filters: { ...prev.filters, [field]: value } }));
    };

    onFilter = () => {
        this.setState(prev => ({
            appliedFilters: { ...prev.filters },
            reloadKey: Date.now(),
            selectedIds: [],
            currentPageRows: [],
            seenIds: [],
            allSeenEnabledIds: [],
            page: 0,
            isPageAddedOrEdited: false,
        }));
    };

    applyFieldFilter = (value) => {
        this.setState(prev => {
            const newFieldFilter = prev.appliedFilters.fieldFilter === value ? '' : value;
            return {
                filters: { ...prev.filters, fieldFilter: newFieldFilter },
                appliedFilters: { ...prev.appliedFilters, fieldFilter: newFieldFilter },
                reloadKey: Date.now(),
                selectedIds: [],
                currentPageRows: [],
                seenIds: [],
                allSeenEnabledIds: [],
                page: 0,
                isPageAddedOrEdited: false,
            };
        });
    };

    onGroupsChange = (codes) => {
        this.setState({ userGroupsValue: codes || '', reloadKey: Date.now(), selectedIds: [], currentPageRows: [], seenIds: [], allSeenEnabledIds: [], page: 0, isPageAddedOrEdited: false });
    };

    getPmMembers = () => {
        const { currentUser } = this.props.userStore;
        if (currentUser?.user_type !== 'admin' && currentUser?.Manages?.length) {
            return currentUser.Manages.join(',');
        }
        return '';
    };

    fetchFieldCounts = async (fieldValue, leaveTypeCode) => {
        try {
            const counts = await this.props.userStore.loadUsersFieldCount({
                fieldFilterName: fieldValue,
                pmMembers: this.getPmMembers(),
                ...(fieldValue === 'leave_setting' ? { leaveTypeCode } : {}),
            });
            this.setState({ fieldCounts: counts });
        } catch (err) {
            console.error(err);
        }
    };

    fetchRows = async (params = {}) => {
        const { appliedFilters, userGroupsValue, selectedField, selectedLeaveTypeCode } = this.state;
        const _params = {
            ...params,
            name: appliedFilters.name || '',
            groupCodes: userGroupsValue || '',
            pmMembers: this.getPmMembers(),
            fieldName: selectedField.value,
            ...(appliedFilters.fieldFilter !== '' ? { fieldFilterName: selectedField.value, fieldFilterValue: appliedFilters.fieldFilter } : {}),
            ...(selectedField.value === 'leave_setting' ? { leaveTypeCode: selectedLeaveTypeCode } : {}),
        };
        const response = await this.props.userStore.loadUsersList(_params);
        const list = response.rows || [];

        if (selectedField) {
            const enabledIdSet = new Set(list.filter(r => !!r[selectedField.value]).map(r => r.id));
            const pageIds = list.map(r => r.id);
            this.setState(prev => {
                const seenIdSet = new Set(prev.seenIds);
                const newIds = pageIds.filter(id => !seenIdSet.has(id));
                const newEnabledIds = newIds.filter(id => enabledIdSet.has(id));
                return {
                    currentPageRows: list,
                    seenIds: Array.from(new Set([...prev.seenIds, ...pageIds])),
                    allSeenEnabledIds: Array.from(new Set([...prev.allSeenEnabledIds, ...newEnabledIds])),
                    selectedIds: Array.from(new Set([...prev.selectedIds, ...newEnabledIds])),
                };
            });
        } else {
            this.setState({ currentPageRows: list });
        }
        return response;
    };

    handleSingleAction = (id, enable) => {
        const { selectedField, selectedLeaveTypeCode } = this.state;
        const { userStore, commonStore, t } = this.props;
        const fieldName = selectedField.value;
        const fieldLabel = selectedField.label;

        confirmAlert({
            title: enable ? t('Confirm') + ' ' + t('Enable') : t('Confirm') + ' ' + t('Disable'),
            message: t(enable ? 'Are you sure you want to enable' : 'Are you sure you want to disable') + ` ${fieldLabel} ` + t('for the selected member?'),
            buttons: [
                {
                    label: t('Yes'),
                    onClick: async () => {
                        this.setState({ loading: true });
                        try {
                            const res = await userStore.bulkUpdateUserField({
                                field: fieldName,
                                enableIds: enable ? [id] : [],
                                disableIds: enable ? [] : [id],
                                ...(fieldName === 'leave_setting' ? { leaveTypeCode: selectedLeaveTypeCode } : {}),
                            });
                            commonStore.addNotification(t('Saved'), null, 'success');
                            this.setState(prev => ({
                                selectedIds: enable
                                    ? Array.from(new Set([...prev.selectedIds, id]))
                                    : prev.selectedIds.filter(x => x !== id),
                                allSeenEnabledIds: enable
                                    ? Array.from(new Set([...prev.allSeenEnabledIds, id]))
                                    : prev.allSeenEnabledIds.filter(x => x !== id),
                                currentPageRows: prev.currentPageRows.map(r =>
                                    r.id === id ? { ...r, [fieldName]: enable } : r
                                ),
                                isPageAddedOrEdited: true,
                            }));
                            this.fetchFieldCounts(fieldName, selectedLeaveTypeCode);
                        } catch (err) {
                            commonStore.addNotification(err.message || t('Error saving'), null, 'error');
                        } finally {
                            this.setState({ loading: false });
                        }
                    },
                },
                {
                    label: t('No'),
                    onClick: () => { },
                },
            ],
        });
    };

    toggleSelect = (id) => {
        const { selectedIds, selectedField, allSeenEnabledIds, fieldConfig } = this.state;
        const selectedFieldDef = BULK_FIELDS.find(f => f.value === selectedField?.value);
        const isBooleanField = selectedFieldDef?.type === 'boolean';
        const config = (selectedField && isBooleanField) ? fieldConfig[selectedField.value] : null;
        const isSelected = selectedIds.includes(id);
        const isDbEnabled = allSeenEnabledIds.includes(id);

        const isModalField = selectedFieldDef?.type === 'modal';
        if (isModalField && isSelected && isDbEnabled && this.state.appliedFilters.fieldFilter !== 'true') {
            this.setState({ deactivateUserId: id });
            return;
        }

        if (config) {
            const isUnchecking = isSelected && isDbEnabled;
            const isChecking = !isSelected && !isDbEnabled;

            if (isUnchecking && config.singleDisable) {
                this.handleSingleAction(id, false);
                return;
            }
            if (isChecking && config.singleEnable) {
                this.handleSingleAction(id, true);
                return;
            }
        }

        this.setState(prev => ({
            selectedIds: prev.selectedIds.includes(id)
                ? prev.selectedIds.filter(x => x !== id)
                : [...prev.selectedIds, id],
        }));
    };

    toggleSelectAll = () => {
        this.setState(prev => {
            const { currentPageRows, selectedIds } = prev;
            const allIds = currentPageRows.map(r => r.id);
            const selectedSet = new Set(selectedIds);
            const allSelected = allIds.length > 0 && allIds.every(id => selectedSet.has(id));
            if (allSelected) {
                const allIdSet = new Set(allIds);
                return { selectedIds: selectedIds.filter(id => !allIdSet.has(id)) };
            }
            return { selectedIds: Array.from(new Set([...selectedIds, ...allIds])) };
        });
    };

    applyBulkUpdate = () => {
        const { selectedIds, selectedField, seenIds, allSeenEnabledIds, selectedLeaveTypeCode } = this.state;
        const { userStore, commonStore, t } = this.props;
        const selectedSet = new Set(selectedIds);
        const enabledSet = new Set(allSeenEnabledIds);
        const toEnable = seenIds.filter(id => selectedSet.has(id) && !enabledSet.has(id));
        const toDisable = seenIds.filter(id => !selectedSet.has(id) && enabledSet.has(id));
        if (!toEnable.length && !toDisable.length) {
            return commonStore.addNotification(t('No changes to save'), null, 'warning');
        }
        confirmAlert({
            title: t('Confirm Update'),
            message: (
                <div>
                    {toEnable.length > 0 && <div>{t('Enabling')} {t('for')}: {toEnable.length} {t('Users')}</div>}
                    {toDisable.length > 0 && <div>{t('Disabling')} {t('for')}: {toDisable.length} {t('Users')}</div>}
                </div>
            ),
            buttons: [
                {
                    label: t('Yes'),
                    onClick: async () => {
                        this.setState({ loading: true });
                        try {
                            const res = await userStore.bulkUpdateUserField({
                                field: selectedField.value,
                                enableIds: toEnable,
                                disableIds: toDisable,
                                ...(selectedField.value === 'leave_setting' ? { leaveTypeCode: selectedLeaveTypeCode } : {}),
                            });
                            commonStore.addNotification(t(res.message), null, 'success');
                            this.setState({
                                selectedIds: [],
                                seenIds: [],
                                allSeenEnabledIds: [],
                                currentPageRows: [],
                                reloadKey: Date.now(),
                                isPageAddedOrEdited: true,
                            });
                            this.fetchFieldCounts(selectedField.value, selectedLeaveTypeCode);
                        } catch (err) {
                            commonStore.addNotification(err.message || t('Error saving'), null, 'error');
                        } finally {
                            this.setState({ loading: false });
                        }
                    },
                },
                { label: t('No'), onClick: () => { } },
            ],
        });
    };

    renderCheckboxCell = (row) => {
        const userId = row.original.id;
        const { selectedIds } = this.state;
        return (
            <div className="d-flex align-items-center justify-content-center">
                <Checkbox
                    inlineBlock
                    isChecked={selectedIds.includes(userId)}
                    onChange={() => this.toggleSelect(userId)}
                    wrapperClassName="checkbox-position-fix"
                    labelClassName="ps-0"
                />
            </div>
        );
    };

    renderFieldValueCell = (row) => {
        const { selectedField } = this.state;
        if (!selectedField) return null;
        const val = !!row.original[selectedField.value];
        return (
            <div className={`status-box ${val ? 'status-box_active' : 'status-box_default'}`}>
                {val ? this.props.t('enabled') : this.props.t('disabled')}
            </div>
        );
    };

    render() {
        const { t } = this.props;
        const {
            selectedField, selectedLeaveTypeCode, filters, appliedFilters, loading,
            selectedIds, currentPageRows, seenIds, allSeenEnabledIds, reloadKey, fieldConfig, userGroupsValue, fieldCounts,
            bulkModalOpen, bulkDeactivateModalOpen, deactivateUserId,
            weeklyOvertimeSettingMap, overtimeCapSettingMap,
        } = this.state;

        const activeSettingMap = selectedField?.value === 'allow_weekly_overtime' ? weeklyOvertimeSettingMap
            : selectedField?.value === 'allow_overtime_cap_settings' ? overtimeCapSettingMap
                : {};

        if (loading) return <LoadingSpinner />;

        const selectedFieldDef = BULK_FIELDS.find(f => f.value === selectedField?.value);
        const isBooleanField = selectedFieldDef?.type === 'boolean';
        const isModalField = selectedFieldDef?.type === 'modal';

        const ActiveModal = selectedFieldDef?.modalComponent || null;
        const activeFieldConfig = (selectedField && isBooleanField) ? (fieldConfig[selectedField.value] || {}) : {};
        const selectedSet = new Set(selectedIds);
        const enabledSet = new Set(allSeenEnabledIds);
        const allIds = currentPageRows.map(r => r.id);
        const isAllSelected = allIds.length > 0 && allIds.every(id => selectedSet.has(id));
        const willEnableCount = seenIds.filter(id => selectedSet.has(id) && !enabledSet.has(id)).length;
        const willDisableCount = seenIds.filter(id => !selectedSet.has(id) && enabledSet.has(id)).length;

        // Filter-based batch mode helpers — gated on backend config per field
        const allowBatchAction = !!(selectedField && fieldConfig[selectedField.value]?.allow_batch_action_in_next_cycle);
        const isDeactivateFilter = allowBatchAction && appliedFilters.fieldFilter === 'true';
        const isActivateFilter = allowBatchAction && appliedFilters.fieldFilter === 'false';
        const isModalFilterApplied = isDeactivateFilter || isActivateFilter;
        const batchDeactivateIds = isDeactivateFilter ? seenIds.filter(id => enabledSet.has(id)) : [];
        const batchActivateIds = isActivateFilter ? seenIds.filter(id => !enabledSet.has(id)) : [];

        const totalChanges =
            (activeFieldConfig.singleEnable ? 0 : willEnableCount) +
            (activeFieldConfig.singleDisable ? 0 : willDisableCount);

        const modalUser = deactivateUserId ? currentPageRows.find(r => r.id === deactivateUserId) : null;
        const modalUserName = modalUser ? `${modalUser.first_name} ${modalUser.last_name}` : '';

        const dateTimeRules = this.props.commonStore.getDateTimeRules();
        const allowCompanyShifts = this.props.commonStore?.config?.client?.allow_company_shifts;
        const fieldOptions = BULK_FIELDS
            .filter(f => {
                if (f.value === 'allow_weekly_overtime' || f.value === 'allow_overtime_cap_settings') {
                    return allowCompanyShifts;
                }
                return true;
            }).map(f => ({ value: f.value, label: t(f.label) }));
        const fieldFilterOptions = FIELD_FILTER_OPTIONS.map(o => ({ value: o.value, label: t(o.label) }));
        const selectedFieldDescription = selectedFieldDef?.description || '';

        const isLeaveSetting = selectedField?.value === 'leave_setting';
        const leaveTypeOptions = (this.props.commonStore?.config?.allAbsentTypes || [])
            .filter(absentType => absentType.isDefault === false)
            .map(absentType => ({ value: absentType.code, label: t(absentType.name) }));
        const readyToShowList = !!(selectedField && (!isLeaveSetting || selectedLeaveTypeCode));

        const columns = [
            {
                Header: t('Name'),
                id: 'first_name',
                textAlign: 'start',
                accessor: 'first_name',
                isSortable: true,
                Cell: ({ row }) => (
                    <div className="text-start">{row.original.first_name} {row.original.last_name}</div>
                ),
            },
            ...(selectedField ? [{
                Header: t(selectedField.label),
                id: selectedField.value,
                accessor: selectedField.value,
                maxWidth: 130,
                textAlign: 'center',
                Cell: ({ row }) => this.renderFieldValueCell(row),
            }] : []),
            ...(selectedFieldDef?.settingIdKey ? [{
                Header: t('Rule Name'),
                id: 'rule_name',
                accessor: 'rule_name',
                textAlign: 'start',
                Cell: ({ row }) => {
                    const settingId = row.original[selectedFieldDef.settingIdKey];
                    const name = settingId ? (activeSettingMap[settingId] || '-') : '-';
                    if (!settingId) return <div className="text-start text-muted">-</div>;
                    return (
                        <div
                            className="text-start text-primary"
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => this.setState({ deactivateUserId: row.original.id })}
                        >
                            {name}
                        </div>
                    );
                },
            }] : []),
            ...(selectedFieldDef?.hasValidity ? [{
                Header: t('Rule Validity'),
                id: 'validity',
                accessor: 'validity',
                textAlign: 'start',
                Cell: ({ row }) => {
                    const { valid_from, valid_to } = row.original;
                    return (
                        <div className="text-start text-muted small">
                            {(valid_from ? `${t('From')} ${shortDateFormat(valid_from, dateTimeRules)} ` : '- ') +
                                (valid_to ? `- ${t('To')} ${shortDateFormat(valid_to, dateTimeRules)}` : '')}
                        </div>
                    );
                },
            }] : []),
            {
                Header: isDeactivateFilter ? t('enabled') : isActivateFilter ? t('disabled') : t('Enable'),
                id: 'select',
                accessor: 'id',
                maxWidth: 100,
                textAlign: 'center',
                Cell: (cellProps) => this.renderCheckboxCell(cellProps.row),
            }
        ];

        return (
            <div>
                <div style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', paddingTop: '0.75rem' }}>
                <label className="fw-semibold mb-1 mt-1 d-block">{t('Field to update')}</label>
                <Row className="mb-2 align-items-center gx-2">
                    <Col xs={5}>
                        <Select
                            options={fieldOptions}
                            value={selectedField ? fieldOptions.find(o => o.value === selectedField.value) : null}
                            onChange={(opt) => {
                                this.setState(prev => ({
                                    selectedField: opt,
                                    selectedLeaveTypeCode: null,
                                    selectedIds: [],
                                    seenIds: [],
                                    allSeenEnabledIds: [],
                                    reloadKey: Date.now(),
                                    filters: { ...prev.filters, fieldFilter: '' },
                                    appliedFilters: { ...prev.appliedFilters, fieldFilter: '' },
                                    fieldCounts: null,
                                    deactivateUserId: null,
                                    page: 0,
                                    isPageAddedOrEdited: false,
                                }));
                                if (opt && opt.value !== 'leave_setting') this.fetchFieldCounts(opt.value);
                            }}
                        />
                    </Col>
                    {isLeaveSetting && (
                        <Col xs={5}>
                            <Select
                                options={leaveTypeOptions}
                                value={leaveTypeOptions.find(o => o.value === selectedLeaveTypeCode) || null}
                                placeholder={t('Select leave type')}
                                onChange={(opt) => {
                                    const leaveTypeCode = opt?.value || null;
                                    this.setState(prev => ({
                                        selectedLeaveTypeCode: leaveTypeCode,
                                        selectedIds: [],
                                        seenIds: [],
                                        allSeenEnabledIds: [],
                                        reloadKey: Date.now(),
                                        filters: { ...prev.filters, fieldFilter: '' },
                                        appliedFilters: { ...prev.appliedFilters, fieldFilter: '' },
                                        fieldCounts: null,
                                    }));
                                    if (leaveTypeCode) this.fetchFieldCounts('leave_setting', leaveTypeCode);
                                }}
                            />
                        </Col>
                    )}
                    {readyToShowList && fieldCounts && (
                        <Col xs="auto" className="mt-2 mt-sm-0">
                            <Row className="member_status__summary gx-2">
                                <Col xs="auto" className="d-flex justify-content-center justify-content-sm-start">
                                    <div
                                        className="highlightedgreen"
                                        role="button"
                                        onClick={() => this.applyFieldFilter('true')}
                                        style={{ cursor: 'pointer', opacity: appliedFilters.fieldFilter === 'true' ? 0.4 : 1 }}
                                    >
                                        {t('enabled')} : {fieldCounts.enabled}
                                    </div>
                                </Col>
                                <Col xs="auto" className="d-flex justify-content-center">
                                    <div
                                        className="highlightedred"
                                        role="button"
                                        onClick={() => this.applyFieldFilter('false')}
                                        style={{ cursor: 'pointer', opacity: appliedFilters.fieldFilter === 'false' ? 0.4 : 1 }}
                                    >
                                        {t('disabled')} : {fieldCounts.disabled}
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    )}
                    {readyToShowList && (
                        <Col xs="auto" className="d-flex align-items-center gap-2 ms-auto mt-2 mt-sm-0">
                            {allowBatchAction && isModalFilterApplied ? (
                                isDeactivateFilter ? (
                                    <Button fill disabled={batchDeactivateIds.length === 0} onClick={() => this.setState({ bulkDeactivateModalOpen: true })}>
                                        {t('Disable All')} ({batchDeactivateIds.length})
                                    </Button>
                                ) : (
                                    <Button fill disabled={batchActivateIds.length === 0} onClick={() => {
                                        this.setState(prev => ({
                                            selectedIds: Array.from(new Set([...prev.selectedIds, ...batchActivateIds])),
                                            bulkModalOpen: true,
                                        }));
                                    }}>
                                        {t('Enable All')} ({batchActivateIds.length})
                                    </Button>
                                )
                            ) : (
                                <>
                                    {!(activeFieldConfig.singleEnable && activeFieldConfig.singleDisable) && (
                                        <Checkbox
                                            inlineBlock
                                            isChecked={isAllSelected}
                                            onChange={this.toggleSelectAll}
                                            disabled={allIds.length === 0}
                                            label={t('Select All')}
                                        />
                                    )}
                                    {isBooleanField && !(activeFieldConfig.singleEnable && activeFieldConfig.singleDisable) && (
                                        <Button fill disabled={totalChanges === 0} onClick={this.applyBulkUpdate}>
                                            {t('Update')} ({totalChanges})
                                        </Button>
                                    )}
                                    {isModalField && (
                                        <Button fill disabled={willEnableCount === 0} onClick={() => this.setState({ bulkModalOpen: true })}>
                                            {t('Assign')} ({willEnableCount})
                                        </Button>
                                    )}
                                </>
                            )}
                        </Col>
                    )}
                </Row>
                {selectedFieldDescription && (
                    <Row>
                        <Col xs={5}>
                            <span className="help-block">{t(selectedFieldDescription)}</span>
                        </Col>
                    </Row>
                )}
                {selectedField?.value === 'allow_user_for_auto_timesheet'
                    && !!this.props.commonStore?.config?.client?.data?.loginRules?.allowShiftToEnableDisableAutoTimesheet && (
                        <Row className="mt-1 mb-2">
                            <Col xs={12}>
                                <span className="font-size-11 text-muted">
                                    <i className="fa fa-info-circle me-1"></i>
                                    {t('Note')} : {t("Enabling 'Auto Time Recording' will also automatically grant members the Schedule 'Create, Edit & Delete' and 'Edit Auto Time Recording' permissions as suggested settings. These permissions can be modified as needed from Users → More Actions → Bulk User Permissions.")}
                                </span>
                            </Col>
                        </Row>
                    )
                }
                {selectedField &&
                    <Row>
                        <Col xs={12} sm={12} md={9} className={''}>
                            <GenericFilter
                                filters={filters}
                                setFilter={this.setFilter}
                                onFilter={this.onFilter}
                                loading={loading}
                                hasCustomFilter
                                customFilterName={{ value: 'fieldFilter' }}
                                customFilterOptions={fieldFilterOptions}
                                searchFilterPlaceholder={t('Search')}
                            />
                        </Col>
                        <Col xs={{ span: 3, order: 3 }} sm={{ span: 2, order: 3 }} md={{ span: 3, order: 1 }} className={'mt-2'}>
                            <SingleUserGroupsWidget
                                value={userGroupsValue}
                                onChange={this.onGroupsChange}
                            />
                        </Col>
                    </Row>
                }
                </div>

                {readyToShowList &&
                    <GenericList
                        key={reloadKey}
                        columns={columns}
                        requestData={this.fetchRows}
                        filters={appliedFilters}
                        page={this.state.page}
                        isPageAddedOrEdited={this.state.isPageAddedOrEdited}
                        handlePageChange={this.handlePageChange}
                        showPageSizeSelector
                        pageSizeListId="bulk_user_profile_update"
                    />
                }

                {ActiveModal && bulkModalOpen && (
                    <ActiveModal
                        userIds={isActivateFilter ? batchActivateIds : seenIds.filter(id => selectedSet.has(id) && !enabledSet.has(id))}
                        initialTab={selectedFieldDef.initialTab}
                        userNameMap={Object.fromEntries(currentPageRows.map(r => [r.id, `${r.first_name} ${r.last_name}`]))}
                        onHide={() => {
                            this.setState({ bulkModalOpen: false, reloadKey: Date.now(), selectedIds: [], seenIds: [], allSeenEnabledIds: [], isPageAddedOrEdited: true });
                            if (selectedField) this.fetchFieldCounts(selectedField.value);
                        }}
                    />
                )}
                {allowBatchAction && bulkDeactivateModalOpen && (
                    <BulkDeactivateOvertimeModal
                        fieldDef={selectedFieldDef}
                        userIds={batchDeactivateIds}
                        onHide={(success) => {
                            this.setState({ bulkDeactivateModalOpen: false });
                            if (success) {
                                this.setState({ reloadKey: Date.now(), selectedIds: [], seenIds: [], allSeenEnabledIds: [], isPageAddedOrEdited: true });
                                if (selectedField) this.fetchFieldCounts(selectedField.value);
                            }
                        }}
                    />
                )}
                {isModalField && deactivateUserId && (
                    <UserWeeklyOvertimeSettingModal
                        userId={deactivateUserId}
                        userName={modalUserName}
                        initialTab={selectedFieldDef.initialTab}
                        onHide={() => {
                            this.setState({ deactivateUserId: null, reloadKey: Date.now(), selectedIds: [], seenIds: [], allSeenEnabledIds: [], isPageAddedOrEdited: true });
                            if (selectedField) this.fetchFieldCounts(selectedField.value);
                        }}
                    />
                )}
            </div>
        );
    }
}

export default inject('userStore', 'commonStore')(withLocalization(observer(BulkUserProfileUpdate)));
